/**
 * main.ts — Application entry point.
 *
 * Boots the Game instance after the DOM is ready and wires up the
 * start / restart buttons so the player can begin or retry.
 */

import { Game } from './game/Game';
import { MISSION_CATALOG } from './game/Missions';
import {
  isTouchDevice,
  loadMobileSettings,
  saveMobileSettings,
  enterFullscreen,
  tryLockLandscape,
  toggleFullscreen,
} from './game/MobileControls';
import {
  loadDesktopSettings,
  saveDesktopSettings,
  MenuGamepadPoller,
} from './game/Controls';

const game = new Game();

/* ---------- Controller menu navigation (independent of game loop) ------- */
/* Allows the gamepad to cycle missions and start/resume/restart from any   */
/* menu screen even when the normal game loop isn't ticking.               */
const menuPoller = new MenuGamepadPoller();
menuPoller.start();

/* When the game pauses, sync the poller's prev-states so the same button  */
/* press that triggered the pause doesn't immediately re-resume the game.  */
game.onPause = () => menuPoller.syncState();

/* ---------- Mobile detection & settings ---------- */
const mobile = isTouchDevice();
const mobileSettingsPanel = document.getElementById('mobile-settings-panel')!;
const desktopSettingsPanel = document.getElementById('desktop-settings-panel')!;
const desktopHint = document.getElementById('desktop-controls-hint')!;
const gamepadHint = document.getElementById('gamepad-controls-hint')!;
const mobileHint = document.getElementById('mobile-controls-hint')!;
const dpadSideSel = document.getElementById('setting-dpad-side') as HTMLSelectElement;
const inputModeSel = document.getElementById('setting-input-mode') as HTMLSelectElement;
const invertYCheck = document.getElementById('setting-invert-y') as HTMLInputElement;

/* Desktop settings elements */
const mouseSensSlider = document.getElementById('setting-mouse-sensitivity') as HTMLInputElement;
const sensValueLabel = document.getElementById('sensitivity-value')!;
const invertYDesktop = document.getElementById('setting-invert-y-desktop') as HTMLInputElement;
const gamepadStatusEl = document.getElementById('gamepad-status')!;
const mouseModeSel = document.getElementById('setting-mouse-mode') as HTMLSelectElement;

/* ---------- Desktop settings ---------- */
if (!mobile) {
  desktopSettingsPanel.style.display = '';
  mobileSettingsPanel.style.display = 'none';
  mobileHint.style.display = 'none';

  const dSettings = loadDesktopSettings();
  mouseSensSlider.value = String(dSettings.mouseSensitivity);
  sensValueLabel.textContent = String(dSettings.mouseSensitivity);
  invertYDesktop.checked = dSettings.invertY;
  mouseModeSel.value = dSettings.mouseMode;

  mouseSensSlider.addEventListener('input', () => {
    const s = loadDesktopSettings();
    s.mouseSensitivity = Number(mouseSensSlider.value);
    sensValueLabel.textContent = mouseSensSlider.value;
    saveDesktopSettings(s);
  });
  invertYDesktop.addEventListener('change', () => {
    const s = loadDesktopSettings();
    s.invertY = invertYDesktop.checked;
    saveDesktopSettings(s);
  });
  mouseModeSel.addEventListener('change', () => {
    const s = loadDesktopSettings();
    s.mouseMode = mouseModeSel.value as 'direct' | 'auto';
    saveDesktopSettings(s);
    // Live-update if a game is paused
    if (game.controls) game.controls.desktopSettings = s;
  });

  /* Gamepad detection feedback on the start screen */
  const updateGamepadStatus = () => {
    try {
      const pads = navigator.getGamepads();
      let found = false;
      for (let i = 0; i < pads.length; i++) {
        const gp = pads[i];
        if (gp && gp.mapping === 'standard') {
          gamepadStatusEl.textContent = `✓ ${gp.id.substring(0, 36)}`;
          gamepadStatusEl.style.color = '#56ffa4';
          gamepadHint.style.display = '';
          desktopHint.style.display = 'none';
          found = true;
          break;
        }
      }
      if (!found) {
        gamepadStatusEl.textContent = 'Not detected';
        gamepadStatusEl.style.color = '#7eb3ff';
        gamepadHint.style.display = 'none';
        desktopHint.style.display = '';
      }
    } catch { /* ignore */ }
  };
  window.addEventListener('gamepadconnected', updateGamepadStatus);
  window.addEventListener('gamepaddisconnected', updateGamepadStatus);
  updateGamepadStatus();
} else {
  desktopSettingsPanel.style.display = 'none';
  desktopHint.style.display = 'none';
  gamepadHint.style.display = 'none';

  mobileSettingsPanel.style.display = '';
  mobileHint.style.display = '';

  const settings = loadMobileSettings();
  dpadSideSel.value = settings.dpadSide;
  inputModeSel.value = settings.inputMode;
  invertYCheck.checked = settings.invertY;

  dpadSideSel.addEventListener('change', () => {
    const s = loadMobileSettings();
    s.dpadSide = dpadSideSel.value as 'left' | 'right';
    saveMobileSettings(s);
  });
  inputModeSel.addEventListener('change', () => {
    const s = loadMobileSettings();
    s.inputMode = inputModeSel.value as 'dpad' | 'motion';
    saveMobileSettings(s);
  });
  invertYCheck.addEventListener('change', () => {
    const s = loadMobileSettings();
    s.invertY = invertYCheck.checked;
    saveMobileSettings(s);
  });

  document.getElementById('fullscreen-btn')!.addEventListener('click', async () => {
    await toggleFullscreen();
  });
}

/* ---------- Start mission picker ---------- */
const startMissionSelect = document.getElementById('start-mission-select') as HTMLSelectElement;
for (let i = 0; i < MISSION_CATALOG.length; i++) {
  const option = document.createElement('option');
  option.value = String(i);
  option.textContent = `${i + 1}. ${MISSION_CATALOG[i]}`;
  startMissionSelect.appendChild(option);
}
startMissionSelect.value = '0';

/* ---------- Start button ---------- */
document.getElementById('start-btn')!.addEventListener('click', async () => {
  /* If the game is paused, resume instead of starting fresh */
  if (game.paused) {
    game.togglePause();
    return;
  }

  const startMissionIdx = Number.parseInt(startMissionSelect.value, 10) || 0;
  game.setStartMission(startMissionIdx);
  document.getElementById('start-screen')!.classList.add('hidden');

  if (mobile) {
    await enterFullscreen();
    await tryLockLandscape();
  }

  game.start();
});

/* ---------- Restart-while-paused button ---------- */
document.getElementById('restart-pause-btn')!.addEventListener('click', () => {
  document.getElementById('restart-pause-btn')!.classList.add('hidden');
  document.getElementById('start-screen')!.classList.add('hidden');
  document.getElementById('start-btn')!.textContent = 'Take Off';
  game.restart();
});

/* ---------- Restart button ---------- */
document.getElementById('restart-btn')!.addEventListener('click', () => {
  document.getElementById('game-over')!.classList.add('hidden');
  game.restart();
});

/* ---------- Next-mission button ---------- */
document.getElementById('next-mission-btn')!.addEventListener('click', () => {
  const nextMissionSelect = document.getElementById('next-mission-select') as HTMLSelectElement;
  const nextMissionIdx = Number.parseInt(nextMissionSelect.value, 10);
  document.getElementById('mission-complete')!.classList.add('hidden');
  game.nextMission(Number.isNaN(nextMissionIdx) ? undefined : nextMissionIdx);
});
